import React from 'react';
import moment from 'moment';
import styled from 'styled-components';
import * as Yup from 'yup';
import { useFormik } from 'formik';

import DisplayLocation from '~/src/components/common/DisplayLocation';
import TitleComponent from '~/src/components/common/forms/Title/TitleComponent';
import ProductService from '~/src/services/logic/ProductService';
import { DATE_FORMAT, ROLES } from '~/src/utils/constants';
import { Colors } from '~/src/scss/common/style';
import { LocationWithQuantitiesDto, PalletDescriptionEditDto } from '../PalletDescriptions';
import TextInputComponent from '~/src/components/common/forms/TextInput/TextInputComponent';
import PalletDescriptionsApi from '~/src/services/api/PalletDescriptionsApi';
import { StatusCodes } from 'http-status-codes';
import RolesDependent from '~/src/components/common/RolesDependent';

interface Props {
  item: LocationWithQuantitiesDto;
  showForm: boolean;
  index: number;
}
export default function PalletDescription({ item, index, showForm = true }: Props): JSX.Element {
  const totalQuantities = item.groupedQuantities.reduce((sum, curr) => {
    const quantitiesCu = curr.quantities.reduce((sum, curr) => {
      return sum + (curr.totalCuUnits ?? 0);
    }, 0);

    const du = ProductService.getDu(curr.product.packagedProducts);

    const quantities = Math.ceil(quantitiesCu / (du?.numberSmallerUnits ?? 1));

    return sum + quantities;
  }, 0);

  const totalKg = item.groupedQuantities.reduce((sum, curr) => {
    const quantitiesCu = curr.quantities.reduce((sum, item) => {
      return sum + (item.totalCuUnits ?? 0);
    }, 0);

    const du = ProductService.getDu(curr.product.packagedProducts);

    const quantities = Math.ceil(quantitiesCu / (du?.numberSmallerUnits ?? 1));

    const kg = ((du?.grossWeight ?? 0) * quantities) / 1000;
    return Math.round(100 * (sum + kg)) / 100;
  }, 0);

  const updateSortOrderNumber = async (sortOrderNumber: number): Promise<void> => {
    const vals: PalletDescriptionEditDto = {
      orderId: item.palletDescriptionObject.orderId,
      sortOrderNumber: sortOrderNumber,
      name: item.palletDescriptionObject.name,
      locationId: item.palletDescriptionObject.locationId,
      palletId: item.palletDescriptionObject.palletId,
    };

    const result = await PalletDescriptionsApi.change(item.palletDescriptionObject.id, vals);
    if (result.status === StatusCodes.OK) {
      if (result.data.message !== '') {
        alert(result.data.message);
        history.go(0);
      }
    } else {
      formik.setFieldValue('sortOrderNumber', '');
    }
  };

  const formik = useFormik({
    initialValues: {
      sortOrderNumber: item.palletDescriptionObject.sortOrderNumber ?? '',
    },
    validateOnBlur: true,
    validationSchema: Yup.object({
      sortOrderNumber: Yup.number().typeError('Must be a number').required('Required'),
    }),
    onSubmit: (values) => {
      updateSortOrderNumber(values.sortOrderNumber);
    },
  });

  return (
    <div style={{ marginBottom: 50 }}>
      <RolesDependent
        roles={[
          ROLES.ADMIN,
          ROLES.SUPER_ADMIN,
          ROLES.WAREHOUSE_ADMIN,
          ROLES.WAREHOUSE,
          ROLES.OFFICE,
          ROLES.SALES,
          ROLES.DATABASE_ADMIN,
          ROLES.SVILA_CLIENT_ADMIN,
          ROLES.SVILA_CLIENT,
        ]}
      >
        {showForm ? (
          <form onSubmit={formik.handleSubmit}>
            <TextInputComponent
              name="sortOrderNumber"
              label={`Pallet Number for location ${item.location.chamberNumber}-${item.location.column}-${item.location.letter}-${item.location.level} in warehouse ${item.palletDescriptionObject.warehouse.name}:`}
              errors={formik.errors}
              value={formik.values.sortOrderNumber}
              onChange={formik.handleChange}
              onBlur={formik.handleSubmit}
              cols={12}
              inline
            />
          </form>
        ) : (
          <TitleComponent title={`Pallet #${item.palletDescriptionObject.sortOrderNumber}`} />
        )}
      </RolesDependent>
      <Table>
        <thead>
          <tr>
            <HeaderCell>#</HeaderCell>
            <HeaderCell>Product Description</HeaderCell>
            <HeaderCell>Pieces/Case</HeaderCell>
            <HeaderCell>Net Weight Single Piece</HeaderCell>
            <HeaderCell>Gross Weight Carton</HeaderCell>
            <HeaderCell>Quantity</HeaderCell>
            <HeaderCell>Cases/Layer</HeaderCell>
            <HeaderCell>Layers</HeaderCell>
            <HeaderCell>Expiry Date</HeaderCell>
            <HeaderCell>KG</HeaderCell>
          </tr>
        </thead>
        <TBodyStyles>
          {item.groupedQuantities.map((info) => {
            const du = ProductService.getDu(info.product.packagedProducts);
            const cu = ProductService.getCu(info.product.packagedProducts);
            const layer = ProductService.getLayer(info.product.packagedProducts);

            const quantitiesCu = info.quantities.reduce((sum, curr) => {
              return sum + (curr.totalCuUnits ?? 0);
            }, 0);

            const quantities = Math.ceil(quantitiesCu / (du?.numberSmallerUnits ?? 1));

            const layerCount = Math.round(100 * (quantities / (layer?.numberSmallerUnits ?? 1))) / 100;
            const kg = parseFloat((((du?.grossWeight ?? 0) * quantities) / 1000).toFixed(2));

            if (quantities === 0) {
              return null;
            }

            return (
              <TrStyle key={index}>
                <Cell width="10">{`#${index + 1}`}</Cell>
                <Cell align="left">{info.product.genericProduct.name}</Cell>
                <Cell width="100">{du?.numberSmallerUnits}</Cell>
                <Cell width="100">{cu?.netWeight}</Cell>
                <Cell width="100">{du?.grossWeight}</Cell>
                <Cell width="100">{quantities} Du</Cell>
                <Cell width="100">{layer?.numberSmallerUnits ?? ''}</Cell>
                <Cell width="100">{layerCount}</Cell>
                <Cell width="100">
                  {info.quantities[0].expirationDate
                    ? moment(info.quantities[0].expirationDate).format(DATE_FORMAT)
                    : null}
                </Cell>
                <Cell width="100">{kg}</Cell>
              </TrStyle>
            );
          })}
          <TrStyle key={index}>
            <Cell></Cell>
            <Cell align="left">Total For Pallet</Cell>
            <Cell></Cell>
            <Cell></Cell>
            <Cell></Cell>
            <Cell>{totalQuantities} Du</Cell>
            <Cell></Cell>
            <Cell></Cell>
            <Cell></Cell>
            <Cell>{totalKg}</Cell>
          </TrStyle>
        </TBodyStyles>
      </Table>
    </div>
  );
}

interface CellProps {
  disabled?: boolean;
  align?: 'center' | 'left' | 'right';
  width?: string;
}

const Cell = styled.td<CellProps>`
  padding: 5px;
  white-space: pre-line;
  text-align: ${(props): string => (props.align ? props.align : 'center')};
  opacity: ${(props): number => (props.disabled ? 0.3 : 1)};
  width: ${(props): string => (props.width ? props.width + 'px' : 'auto')};
  &:first:child {
    display: flex;
    flex-direction: column;
  }
`;

const CellRow = styled.div`
  flex-direction: row;
  display: flex;
`;
const TrStyle = styled.tr`
  border-bottom: 1px solid #6e6e6e;
`;

const Table = styled.div`
  display: table;
  width: 100%;
  td {
    padding: 13px;
    font-size: 14px;
  }
  tbody tr:nth-child(even) {
    background-color: #fbfbfb;
  }
  tbody tr:nth-child(odd) {
    background-color: #f4f4f4;
  }
`;

const HeaderCell = styled.td`
  padding: 5px;
  color: ${Colors.darkBrown};
  font-weight: bold;
  white-space: pre-line;
  text-align: center;
`;

const TBodyStyles = styled.tbody`
  overflow: hidden;
  border-radius: 10px;
  tr:nth-child(even) {
    background-color: #fbfbfb;
  }
  tr:nth-child(odd) {
    background-color: #f4f4f4;
  }
`;
